import { cn } from "@/lib/utils";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { X } from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

/** Titled form section card. */
export const FormSection = ({ title, description, children }) => (
  <section className="rounded-xl border border-border bg-card p-6 shadow-card">
    <div className="mb-5">
      <h3 className="text-base font-bold text-foreground">{title}</h3>
      {description && <p className="mt-1 text-sm text-muted-foreground">{description}</p>}
    </div>
    <div className="space-y-4">{children}</div>
  </section>
);

/** Labeled field wrapper with inline error. */
export const Field = ({ label, htmlFor, error, hint, children, className }) => (
  <div className={cn("space-y-1.5", className)}>
    {label && (
      <Label htmlFor={htmlFor} className="text-sm">
        {label}
      </Label>
    )}
    {children}
    {hint && !error && <p className="text-xs text-muted-foreground">{hint}</p>}
    {error && <p className="text-xs font-medium text-destructive" data-testid={`error-${htmlFor}`}>{error}</p>}
  </div>
);

/** Multi-select via toggleable chips (for fixed option lists). */
export const ChipMultiSelect = ({ options, value = [], onChange, testid }) => {
  const toggle = (v) => {
    onChange(value.includes(v) ? value.filter((x) => x !== v) : [...value, v]);
  };
  return (
    <div className="flex flex-wrap gap-2" data-testid={testid}>
      {options.map((opt) => {
        const active = value.includes(opt.value);
        return (
          <button
            key={opt.value}
            type="button"
            data-testid={`${testid}-${opt.value}`}
            data-active={active}
            onClick={() => toggle(opt.value)}
            className={cn(
              "rounded-full border px-3.5 py-1.5 text-sm font-medium transition-colors",
              active
                ? "border-brand-navy bg-brand-navy text-white"
                : "border-border bg-background text-foreground hover:border-brand-navy/40 hover:bg-secondary",
            )}
          >
            {opt.label}
          </button>
        );
      })}
    </div>
  );
};

/** Add-and-remove selector for US states (Select to add, chips to remove). */
export const StatePicker = ({ options, value = [], onChange, testid }) => {
  const available = options.filter((s) => !value.includes(s));
  return (
    <div className="space-y-2" data-testid={testid}>
      <Select value="" onValueChange={(s) => s && onChange([...value, s])}>
        <SelectTrigger className="w-full sm:w-48" data-testid={`${testid}-add`}>
          <SelectValue placeholder="Add a state…" />
        </SelectTrigger>
        <SelectContent className="max-h-64">
          {available.map((s) => (
            <SelectItem key={s} value={s}>{s}</SelectItem>
          ))}
        </SelectContent>
      </Select>
      {value.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {value.map((s) => (
            <Badge key={s} variant="secondary" className="gap-1 py-1">
              {s}
              <button type="button" onClick={() => onChange(value.filter((x) => x !== s))} aria-label={`Remove ${s}`}>
                <X className="h-3 w-3" />
              </button>
            </Badge>
          ))}
        </div>
      )}
    </div>
  );
};
